import React, { useReducer, Dispatch, ReactNode } from 'react'
import classNames from 'classnames/bind'
import { Card, SnackbarIntent } from '@babylon/core-ui'
import { AlertFilled, TickCircle } from '@babylon/icons'
import { useFormatMessage } from '@babylon/intl'
import * as actions from './data/actions'
import MemberContext from './data/context'
import reducer, { Reducer } from './data/reducer'
import FamilyMembers from './FamilyMembers'
import MemberCard from './MemberCard'
import messages from './SearchResultItem.messages'
import styles from './styles.module.scss'

const cx = classNames.bind(styles)
const baseClassName = 'searchResultItem'

export type SetSnackbarMessage = (
  message?: string,
  icon?: ReactNode,
  intent?: SnackbarIntent
) => void

export interface SearchResultItemProps extends Member {
  expanded: boolean
  onFamilyMembersToggle(id: string): void
  setSnackbarMessage: SetSnackbarMessage
}

const getDispatchers = (
  dispatch: Dispatch<actions.AllActions>,
  setSnackbarMessage: SetSnackbarMessage
) => ({
  invitationResent: (message?: string) => (
    patientId?: string,
    error?: string
  ) => {
    if (patientId) {
      setSnackbarMessage(
        message,
        <TickCircle className={styles.snackbar__icon} />
      )
    } else if (error) {
      setSnackbarMessage(
        error,
        <AlertFilled className={styles.snackbar__icon} />,
        'error'
      )
    }
  },
  updateOwner: (transferMessage: string) => (
    newOwnerId?: string,
    error?: string
  ) => {
    if (newOwnerId) {
      dispatch(actions.updateOwner(newOwnerId))
      setSnackbarMessage(
        transferMessage,
        <TickCircle className={styles.snackbar__icon} />
      )
    } else if (error) {
      setSnackbarMessage(
        error,
        <AlertFilled className={styles.snackbar__icon} />,
        'error'
      )
    }
  },
  setFamilyMembers: (members: Member[]) =>
    dispatch(actions.setFamilyMembers(members)),
})

const SearchResultItem = ({
  expanded,
  onFamilyMembersToggle,
  setSnackbarMessage,
  ...props
}: SearchResultItemProps) => {
  const [{ familyMembers, member }, dispatch] = useReducer<Reducer>(reducer, {
    member: { ...props },
    familyMembers: [],
  })
  const { updateOwner, setFamilyMembers, invitationResent } = getDispatchers(
    dispatch,
    setSnackbarMessage
  )
  const fm = useFormatMessage()
  const transferMessage = fm(messages.snack_transfer_ownership_success)
  const invitationMessage = fm(messages.snack_invitation_resent_success)

  const itemClassNames = cx(baseClassName, {
    [`${baseClassName}--expanded`]: expanded,
  })
  const familyClassNames = cx(`${baseClassName}__family`, {
    [`${baseClassName}__family--expanded`]: expanded,
  })

  return (
    <div data-testid="search-result">
      <MemberContext.Provider value={{ member, familyMembers }}>
        <Card className={itemClassNames}>
          <MemberCard
            {...member}
            isMain
            expanded={expanded}
            onDetailsCopy={setSnackbarMessage}
            onResendInvitationEmail={invitationResent(invitationMessage)}
            onTransferOwnership={updateOwner(transferMessage)}
            onFamilyMembersToggle={onFamilyMembersToggle}
          />
          {expanded && props.family_accounts_member_ids.length && (
            <div className={familyClassNames}>
              <FamilyMembers
                ids={props.family_accounts_member_ids}
                onDetailsCopy={setSnackbarMessage}
                onFamilyMembersLoaded={setFamilyMembers}
                onTransferOwnership={updateOwner(transferMessage)}
                onResendInvitationEmail={invitationResent(invitationMessage)}
              />
            </div>
          )}
        </Card>
      </MemberContext.Provider>
    </div>
  )
}

export default SearchResultItem
